import React, { useEffect, useState } from "react";
import {
    Plus,
    Edit,
    Trash2,
    Eye,
    Download,
    Upload,
    X,
    Search,
    FileText,
    Clock,
    Zap,
    Tag,
    DollarSign,
    BookOpen,
    Save,
    AlertCircle,
    CheckSquare,
    Play,
    Award,
    ChevronRight,
    Check,
    CheckCircle,
    XCircle,
    User,
    Mail,
    Globe
} from "react-feather";
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    onSnapshot,
    query,
    where,
    orderBy,
    Timestamp
} from "firebase/firestore";
import {
    ref,
    uploadBytes,
    uploadBytesResumable,
    getDownloadURL,
    deleteObject
} from "firebase/storage";
import { db, storage } from "shared/utils/firebase";
import ResourceViewerModal from "shared/components/ResourceViewerModal";
import PageHeader from "shared/components/PageHeader/PageHeader"; 
import styles from "./ProjectManagement.module.css"; 

function ProjectManagement() {
    const [projects, setProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterDifficulty, setFilterDifficulty] = useState("all");
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState(null);
    const [uploadingPDF, setUploadingPDF] = useState(false);
    const [pdfProgress, setPdfProgress] = useState(0);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [videoProgress, setVideoProgress] = useState(0);
    const [showViewModal, setShowViewModal] = useState(false);
    const [adminViewerData, setAdminViewerData] = useState({ show: false, title: "", url: "", type: "pdf" });

    const openAdminViewer = (title, url, type = "pdf") => {
        if (!url) return;
        setAdminViewerData({ show: true, title, url, type });
    };
    const [viewingProject, setViewingProject] = useState(null);

    // CSV Upload States
    const [showCSVUpload, setShowCSVUpload] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [csvUploadStatus, setCsvUploadStatus] = useState({ message: "", type: "" });
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

    // Self-Project Requests States
    const [activeSubTab, setActiveSubTab] = useState("lab"); // "lab", "requests", or "submissions"
    const [projectRequests, setProjectRequests] = useState([]);
    const [requestFilterStatus, setRequestFilterStatus] = useState("all");

    // Student Project Completion Submissions States
    const [projectSubmissions, setProjectSubmissions] = useState([]);
    const [submissionFilterStatus, setSubmissionFilterStatus] = useState("all");

    const [projectForm, setProjectForm] = useState({
        title: "",
        description: "",
        difficulty: "beginner",
        duration: "",
        category: "",
        skills: [],
        learningOutcomes: ["", "", "", ""],
        prerequisites: ["", ""],
        pdfGuideUrl: "",
        videoUrl: "",
        textManualUrl: "",
        imageUrl: "",
        isPublished: false,
        maxParticipants: "",
        deadline: "",
        siliconPoints: 50
    });

    const difficulties = ["beginner", "intermediate", "advanced"];
    const categories = [
        "Basic Electronics",
        "VLSI Design",
        "Embedded Systems",
        "IoT",
        "Communications & Networking",
        "Artificial Intelligence",
        "Programming Languages",
        "General Tech",
        "Other"
    ];

    useEffect(() => {
        fetchProjects();

        // Real-time listener for Self-Project Requests
        const unsubRequests = onSnapshot(collection(db, "projectRequests"), (snapshot) => {
            const requestsData = snapshot.docs.map(docSnap => ({
                id: docSnap.id,
                ...docSnap.data()
            }));
            setProjectRequests(requestsData);
        }, (error) => {
            console.error("Error listening to project requests:", error);
        });

        // Real-time listener for Student Project Submissions
        const unsubSubmissions = onSnapshot(collection(db, "projectSubmissions"), (snapshot) => {
            const submissionsData = snapshot.docs.map(docSnap => ({
                id: docSnap.id,
                ...docSnap.data()
            }));
            setProjectSubmissions(submissionsData);
        }, (error) => {
            console.error("Error listening to project submissions:", error);
        });

        return () => {
            unsubRequests();
            unsubSubmissions();
        };
    }, []);

    useEffect(() => {
        filterProjects();
    }, [projects, searchTerm, filterDifficulty]);

    const handleApproveSubmission = async (sub) => {
        try {
            // 1. Update projectSubmissions document status to approved
            await updateDoc(doc(db, "projectSubmissions", sub.id), {
                status: "approved",
                approvedAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });

            // 2. Find and update user's registered project status to "completed" in registeredProjects collection
            const regQuery = query(
                collection(db, "registeredProjects"),
                where("userId", "==", sub.userId),
                where("projectId", "==", sub.projectId)
            );
            const regSnapshot = await getDocs(regQuery);
            regSnapshot.forEach(async (docSnap) => {
                await updateDoc(doc(db, "registeredProjects", docSnap.id), {
                    status: "completed",
                    completionPercentage: 100,
                    completedAt: Timestamp.now(),
                    updatedAt: Timestamp.now()
                });
            });

            alert(`Successfully approved completion for "${sub.projectTitle}"! Marked as completed for student.`);
        } catch (err) {
            console.error("Error approving project submission:", err);
            alert("Failed to approve project submission.");
        }
    };

    const handleRejectSubmission = async (sub) => {
        if (window.confirm(`Reject project submission for "${sub.projectTitle}"?`)) {
            try {
                await updateDoc(doc(db, "projectSubmissions", sub.id), {
                    status: "rejected",
                    rejectedAt: Timestamp.now()
                });

                // Update registeredProjects status to changes_requested
                const regQuery = query(
                    collection(db, "registeredProjects"),
                    where("userId", "==", sub.userId),
                    where("projectId", "==", sub.projectId)
                );
                const regSnapshot = await getDocs(regQuery);
                regSnapshot.forEach(async (docSnap) => {
                    await updateDoc(doc(db, "registeredProjects", docSnap.id), {
                        status: "changes_requested"
                    });
                });

                alert("Submission rejected.");
            } catch (err) {
                console.error("Error rejecting submission:", err);
                alert("Failed to reject submission.");
            }
        }
    };

    const handleDeleteSubmission = async (subId) => {
        if (window.confirm("Delete this submission record?")) {
            try {
                await deleteDoc(doc(db, "projectSubmissions", subId));
            } catch (err) {
                console.error("Error deleting submission:", err);
            }
        }
    };

    const handleApproveRequest = async (request) => {
        try {
            // 1. Update project request status to approved
            await updateDoc(doc(db, "projectRequests", request.id), {
                status: "approved",
                approvedAt: Timestamp.now()
            });

            // 2. Map and add project to "projects" collection
            const newProjectRef = await addDoc(collection(db, "projects"), {
                title: request.title,
                description: request.description || "",
                category: request.category || "General Tech",
                difficulty: request.difficulty || "intermediate",
                duration: request.duration || "Self-paced",
                thumbnail: request.thumbnail || "",
                pdfGuideUrl: request.pdfGuideUrl || "",
                videoUrl: request.videoUrl || "",
                labModuleUrl: request.labModuleUrl || "",
                github: request.github || "",
                link: request.link || "",
                skills: request.skills || [],
                learningOutcomes: request.learningOutcomes || [],
                prerequisites: request.prerequisites || [],
                isPublished: true,
                refUser: request.userId,
                userEmail: request.userEmail || "",
                userName: request.userName || "",
                isUserCreated: true,
                createdAt: Timestamp.now()
            });

            // 3. Register user for their approved project in "registeredProjects"
            await addDoc(collection(db, "registeredProjects"), {
                userId: request.userId,
                projectId: newProjectRef.id,
                projectTitle: request.title,
                projectData: {
                    id: newProjectRef.id,
                    title: request.title,
                    description: request.description || "",
                    category: request.category || "General Tech",
                    pdfGuideUrl: request.pdfGuideUrl || "",
                    videoUrl: request.videoUrl || "",
                    labModuleUrl: request.labModuleUrl || ""
                },
                registeredAt: Timestamp.now(),
                status: "registered",
                completionPercentage: 0
            });

            alert(`Successfully approved "${request.title}" and mapped to user's projects!`);
            fetchProjects();
        } catch (err) {
            console.error("Error approving project request:", err);
            alert("Failed to approve project request.");
        }
    };

    const handleRejectRequest = async (request) => {
        if (window.confirm(`Are you sure you want to reject "${request.title}"?`)) {
            try {
                await updateDoc(doc(db, "projectRequests", request.id), {
                    status: "rejected",
                    rejectedAt: Timestamp.now()
                });
                alert(`Project request "${request.title}" rejected.`);
            } catch (err) {
                console.error("Error rejecting project request:", err);
                alert("Failed to reject project request.");
            }
        }
    };

    const handleDeleteRequest = async (requestId) => {
        if (window.confirm("Are you sure you want to delete this project request?")) {
            try {
                await deleteDoc(doc(db, "projectRequests", requestId));
            } catch (err) {
                console.error("Error deleting project request:", err);
            }
        }
    };

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const querySnapshot = await getDocs(collection(db, "projects"));
            const projectsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setProjects(projectsData);
        } catch (error) {
            console.error("Error fetching projects:", error);
        } finally {
            setLoading(false);
        }
    };

    const filterProjects = () => {
        let filtered = projects;

        if (searchTerm) {
            filtered = filtered.filter(project =>
                project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                project.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (filterDifficulty !== "all") {
            filtered = filtered.filter(project => project.difficulty === filterDifficulty);
        }

        setFilteredProjects(filtered);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setProjectForm({
            ...projectForm,
            [name]: type === "checkbox" ? checked : value
        });
    };

    const handleSkillsChange = (e) => {
        const skills = e.target.value.split(",").map(skill => skill.trim());
        setProjectForm({ ...projectForm, skills });
    };

    const handlePDFUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
            alert("Please select a valid PDF file (.pdf)");
            return;
        }

        try {
            setUploadingPDF(true);
            setPdfProgress(5);

            if (!storage) {
                throw new Error("Firebase Storage is not initialized properly.");
            }

            const cleanFileName = file.name.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9.-]/g, "_");
            const storageRef = ref(storage, `project_guides/${Date.now()}_${cleanFileName}`);
            const metadata = { contentType: file.type || "application/pdf" };

            const uploadTask = uploadBytesResumable(storageRef, file, metadata);

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setPdfProgress(progress || 10);
                },
                (error) => {
                    console.error("Firebase Storage Upload Error:", error);
                    alert(`PDF File Upload Error: ${error.message || error.code || "Permission error"}.\n\nMake sure to update your Firebase Storage Rules (see instructions provided).`);
                    setUploadingPDF(false);
                },
                async () => {
                    const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
                    setProjectForm((prev) => ({
                        ...prev,
                        pdfGuideUrl: downloadUrl
                    }));
                    setUploadingPDF(false);
                    setPdfProgress(100);
                }
            );
        } catch (error) {
            console.error("Error starting PDF upload:", error);
            alert(`Failed to upload PDF: ${error.message || "Unknown error"}.`);
            setUploadingPDF(false);
        }
    };

    const handleVideoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith("video/") && !file.name.toLowerCase().match(/\.(mp4|webm|mov|avi|mkv)$/)) {
            alert("Please select a valid video file (.mp4, .webm, .mov, etc.)");
            return;
        }

        try {
            setUploadingVideo(true);
            setVideoProgress(5);

            if (!storage) {
                throw new Error("Firebase Storage is not initialized properly.");
            }

            const cleanFileName = file.name.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9.-]/g, "_");
            const storageRef = ref(storage, `project_videos/${Date.now()}_${cleanFileName}`);
            const metadata = { contentType: file.type || "video/mp4" };

            const uploadTask = uploadBytesResumable(storageRef, file, metadata);

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setVideoProgress(progress || 10);
                },
                (error) => {
                    console.error("Firebase Storage Video Upload Error:", error);
                    alert(`Video File Upload Error: ${error.message || error.code || "Permission error"}.\n\nMake sure to update your Firebase Storage Rules.`);
                    setUploadingVideo(false);
                },
                async () => {
                    const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
                    setProjectForm((prev) => ({
                        ...prev,
                        videoUrl: downloadUrl
                    }));
                    setUploadingVideo(false);
                    setVideoProgress(100);
                }
            );
        } catch (error) {
            console.error("Error starting video upload:", error);
            alert(`Failed to upload video: ${error.message || "Unknown error"}.\n\nTip: You can paste a video URL or Drive link directly.`);
            setUploadingVideo(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!projectForm.title.trim() || !projectForm.description.trim()) {
            alert("Please fill in all required fields");
            return;
        }

        try {
            const projectData = {
                ...projectForm,
                siliconPoints: Number(projectForm.siliconPoints) || 50,
                updatedAt: Timestamp.now()
            };

            if (editingProject) {
                await updateDoc(doc(db, "projects", editingProject.id), projectData);
            } else {
                projectData.createdAt = Timestamp.now();
                await addDoc(collection(db, "projects"), projectData);
            }

            await fetchProjects();
            setShowProjectModal(false);
            resetForm();
        } catch (error) {
            console.error("Error saving project:", error);
            alert("Failed to save project");
        }
    };

    const handleEdit = (project) => {
        setEditingProject(project);
        setProjectForm({
            title: project.title || "",
            description: project.description || "",
            difficulty: project.difficulty || "beginner",
            duration: project.duration || "",
            category: project.category || "",
            skills: project.skills || [],
            learningOutcomes: project.learningOutcomes || ["", "", "", ""],
            prerequisites: project.prerequisites || ["", ""],
            pdfGuideUrl: project.pdfGuideUrl || "",
            videoUrl: project.videoUrl || "",
            textManualUrl: project.textManualUrl || project.labModuleUrl || "",
            imageUrl: project.imageUrl || "",
            isPublished: project.isPublished || false,
            maxParticipants: project.maxParticipants || "",
            deadline: project.deadline || "",
            siliconPoints: project.siliconPoints !== undefined ? Number(project.siliconPoints) : 50
        });
        setShowProjectModal(true);
    };

    const handleView = (project) => {
        setViewingProject(project);
        setShowViewModal(true);
    };

    const handleDelete = async () => {
        try {
            if (projectToDelete.pdfGuideUrl) {
                const pdfRef = ref(storage, projectToDelete.pdfGuideUrl);
                await deleteObject(pdfRef).catch(() => { });
            }

            await deleteDoc(doc(db, "projects", projectToDelete.id));
            await fetchProjects();
            setShowDeleteModal(false);
            setProjectToDelete(null);
        } catch (error) {
            console.error("Error deleting project:", error);
            alert("Failed to delete project");
        }
    };

    const resetForm = () => {
        setProjectForm({
            title: "",
            description: "",
            difficulty: "beginner",
            duration: "",
            category: "",
            skills: [],
            learningOutcomes: ["", "", "", ""],
            prerequisites: ["", ""],
            pdfGuideUrl: "",
            videoUrl: "",
            textManualUrl: "",
            imageUrl: "",
            isPublished: false,
            maxParticipants: "",
            deadline: "",
            siliconPoints: 50
        });
        setEditingProject(null);
        setCsvFile(null);
        setCsvUploadStatus({ message: "", type: "" });
    };

    const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
            case "beginner":
                return "bg-success";
            case "intermediate":
                return "bg-warning";
            case "advanced":
                return "bg-danger";
            default:
                return "bg-secondary";
        }
    };

    const getDifficultyColorHex = (difficulty) => {
        switch (difficulty) {
            case "beginner":
                return "#28a745";
            case "intermediate":
                return "#ffc107";
            case "advanced":
                return "#dc3545";
            default:
                return "#6c757d";
        }
    };

    const downloadCSVTemplate = () => {
        const headers = [
            "Title",
            "Description",
            "Difficulty",
            "Duration",
            "Category",
            "Skills",
            "Learning Outcome 1",
            "Learning Outcome 2",
            "Learning Outcome 3",
            "Learning Outcome 4",
            "Prerequisite 1",
            "Prerequisite 2",
            "Max Participants",
            "Deadline",
            "Published"
        ];

        const sampleData = [
            [
                "IoT Home Automation",
                "Build a smart home system with sensors and controllers",
                "intermediate",
                "4 weeks",
                "IoT",
                "Arduino,IoT,Sensors,C++",
                "Understand IoT principles",
                "Design sensor networks",
                "Build wireless applications",
                "Implement security protocols",
                "Basic C++ knowledge",
                "Understanding of electronics",
                "30",
                "2025-03-31",
                "true"
            ]
        ];

        let csvContent = headers.join(",") + "\n";
        sampleData.forEach(row => {
            csvContent += row.map(cell => `"${cell}"`).join(",") + "\n";
        });

        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "project_template.csv";
        link.click();
    };

    const handleCSVUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.name.endsWith(".csv")) {
            setCsvUploadStatus({
                message: "Only CSV files are accepted. Please save Excel files as CSV format.",
                type: "error"
            });
            return;
        }

        setCsvFile(file);
        setCsvUploadStatus({ message: "", type: "" });
    };

    const parseProjectCSV = (text) => {
        const lines = text.trim().split("\n");
        const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
        const rows = [];

        for (let i = 1; i < lines.length; i++) {
            const line = lines[i];
            let inQuotes = false;
            let currentField = "";
            const fields = [];

            for (let j = 0; j < line.length; j++) {
                const char = line[j];
                const nextChar = line[j + 1];

                if (char === '"') {
                    if (inQuotes && nextChar === '"') {
                        currentField += '"';
                        j++;
                    } else {
                        inQuotes = !inQuotes;
                    }
                } else if (char === "," && !inQuotes) {
                    fields.push(currentField.trim());
                    currentField = "";
                } else {
                    currentField += char;
                }
            }

            fields.push(currentField.trim());

            if (fields.length === headers.length && fields.some(f => f)) {
                const obj = {};
                headers.forEach((header, index) => {
                    obj[header] = fields[index];
                });
                rows.push(obj);
            }
        }

        return rows;
    };

    const processProjectCSVFile = async () => {
        if (!csvFile) return;

        try {
            setCsvUploadStatus({ message: "Processing CSV...", type: "info" });
            const text = await csvFile.text();
            const rows = parseProjectCSV(text);

            if (rows.length === 0) {
                setCsvUploadStatus({
                    message: "CSV file is empty or invalid",
                    type: "error"
                });
                return;
            }

            const row = rows[0];
            setProjectForm({
                title: row.Title || "",
                description: row.Description || "",
                difficulty: row.Difficulty || "beginner",
                duration: row.Duration || "",
                category: row.Category || "",
                skills: row.Skills ? row.Skills.split(",").map(s => s.trim()) : [],
                learningOutcomes: [
                    row["Learning Outcome 1"] || "",
                    row["Learning Outcome 2"] || "",
                    row["Learning Outcome 3"] || "",
                    row["Learning Outcome 4"] || ""
                ],
                prerequisites: [
                    row["Prerequisite 1"] || "",
                    row["Prerequisite 2"] || ""
                ],
                maxParticipants: row["Max Participants"] || "",
                deadline: row.Deadline || "",
                pdfGuideUrl: projectForm.pdfGuideUrl,
                imageUrl: projectForm.imageUrl,
                isPublished: row.Published?.toLowerCase() === "true"
            });

            setCsvUploadStatus({
                message: "Project details imported successfully!",
                type: "success"
            });
        } catch (error) {
            console.error("Error processing CSV:", error);
            setCsvUploadStatus({
                message: "Error processing CSV file",
                type: "error"
            });
        }
    };

    const processBulkProjectCSV = async () => {
        if (!csvFile) {
            setCsvUploadStatus({ message: "Please select a CSV file to import.", type: "error" });
            return;
        }

        try {
            setCsvUploadStatus({ message: "Importing projects...", type: "info" });
            const text = await csvFile.text();
            const rows = parseProjectCSV(text);

            if (rows.length === 0) {
                setCsvUploadStatus({ message: "CSV file is empty or invalid", type: "error" });
                return;
            }

            let added = 0;
            for (const row of rows) {
                const projectData = {
                    title: row.Title || "",
                    description: row.Description || "",
                    difficulty: row.Difficulty || "beginner",
                    duration: row.Duration || "",
                    category: row.Category || "",
                    skills: row.Skills ? row.Skills.split(",").map(s => s.trim()) : [],
                    learningOutcomes: [
                        row["Learning Outcome 1"] || "",
                        row["Learning Outcome 2"] || "",
                        row["Learning Outcome 3"] || "",
                        row["Learning Outcome 4"] || ""
                    ],
                    prerequisites: [row["Prerequisite 1"] || "", row["Prerequisite 2"] || ""],
                    maxParticipants: row["Max Participants"] || "",
                    deadline: row.Deadline || "",
                    pdfGuideUrl: row["PDF Guide URL"] || "",
                    imageUrl: row["Image URL"] || "",
                    isPublished: row.Published?.toLowerCase() === "true",
                    createdAt: Timestamp.now()
                };

                await addDoc(collection(db, "projects"), projectData);
                added++;
            }

            setCsvUploadStatus({ message: `Imported ${added} projects successfully.`, type: "success" });
            setCsvFile(null);
            fetchProjects();
            setShowBulkUploadModal(false);
        } catch (error) {
            console.error("Error importing bulk projects:", error);
            setCsvUploadStatus({ message: "Error importing projects", type: "error" });
        }
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading projects...</p>
            </div>
        );
    }

    return (
        <div className={styles.projectManagement}>
            <PageHeader
                icon={Zap}
                title="Project Management"
                subtitle="Create and manage student projects with guides, resources and submission reviews"
                badge={`${filteredProjects.length} Projects`}
                actions={
                    <>
                        <button
                            className={styles.addBtn + ' ' + styles.uploadBtn}
                            onClick={() => {
                                setCsvFile(null);
                                setCsvUploadStatus({ message: "", type: "" });
                                setShowBulkUploadModal(true);
                            }}
                            style={{ marginRight: '0.5rem' }}
                        >
                            <Upload size={16} className="me-2" />
                            Upload Projects
                        </button>
                        <button
                            className={styles.addBtn}
                            onClick={() => {
                                resetForm();
                                setShowProjectModal(true);
                            }}
                        >
                            <Plus size={16} className="me-2" />
                            Add Project
                        </button>
                    </>
                }
            />

            {/* Sub-tab Toggle: Lab Projects vs Self-Project Requests vs Student Submissions */}
            <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
                <button
                    type="button"
                    className={`${styles.subTabBtn} ${activeSubTab === "lab" ? styles.subTabBtnActive : ""}`}
                    onClick={() => setActiveSubTab("lab")}
                >
                    <Zap size={16} className="me-2" />
                    All Projects ({projects.length})
                </button>

                <button
                    type="button"
                    className={`${styles.subTabBtn} ${activeSubTab === "requests" ? styles.subTabBtnActive : ""}`}
                    onClick={() => setActiveSubTab("requests")}
                    style={{ position: "relative" }}
                >
                    <Clock size={16} className="me-2" />
                    Self-Project Requests ({projectRequests.length})
                    {projectRequests.filter(r => r.status === "pending").length > 0 && (
                        <span style={{
                            background: "#dc3545",
                            color: "white",
                            borderRadius: "50%",
                            padding: "2px 8px",
                            fontSize: "0.75rem",
                            marginLeft: "8px",
                            fontWeight: "bold"
                        }}>
                            {projectRequests.filter(r => r.status === "pending").length}
                        </span>
                    )}
                </button>

                <button
                    type="button"
                    className={`${styles.subTabBtn} ${activeSubTab === "submissions" ? styles.subTabBtnActive : ""}`}
                    onClick={() => setActiveSubTab("submissions")}
                    style={{ position: "relative" }}
                >
                    <CheckSquare size={16} className="me-2" />
                    Student Submissions ({projectSubmissions.length})
                    {projectSubmissions.filter(s => s.status === "pending").length > 0 && (
                        <span style={{
                            background: "#dc3545",
                            color: "white",
                            borderRadius: "50%",
                            padding: "2px 8px",
                            fontSize: "0.75rem",
                            marginLeft: "8px",
                            fontWeight: "bold"
                        }}>
                            {projectSubmissions.filter(s => s.status === "pending").length}
                        </span>
                    )}
                </button>
            </div>

            {activeSubTab === "submissions" ? (
                /* Student Completion Submissions View */
                <div>
                    <div className={styles.filters}>
                        <div className={styles.searchBox}>
                            <Search size={18} className={styles.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search student submissions..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={styles.searchInput}
                            />
                        </div>

                        <div className={styles.filterGroup}>
                            <select
                                value={submissionFilterStatus}
                                onChange={(e) => setSubmissionFilterStatus(e.target.value)}
                                className={styles.filterSelect}
                            >
                                <option value="all">All Submission Statuses</option>
                                <option value="pending">Pending Approval</option>
                                <option value="approved">Approved & Completed</option>
                                <option value="rejected">Rejected / Revision Requested</option>
                            </select>
                        </div>
                    </div>

                    <div className={styles.stats}>
                        <div className={styles.statCard}>
                            <div className={styles.statNumber}>{projectSubmissions.length}</div>
                            <div className={styles.statLabel}>Total Submissions</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statNumber} style={{ color: "#d97706" }}>
                                {projectSubmissions.filter(s => s.status === "pending").length}
                            </div>
                            <div className={styles.statLabel}>Pending Review</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statNumber} style={{ color: "#28a745" }}>
                                {projectSubmissions.filter(s => s.status === "approved").length}
                            </div>
                            <div className={styles.statLabel}>Approved Completed</div>
                        </div>
                    </div>

                    <div className={styles.projectGrid}>
                        {projectSubmissions
                            .filter(sub => {
                                if (submissionFilterStatus !== "all" && sub.status !== submissionFilterStatus) return false;
                                if (searchTerm) {
                                    const q = searchTerm.toLowerCase();
                                    return sub.projectTitle?.toLowerCase().includes(q) ||
                                        sub.userName?.toLowerCase().includes(q) ||
                                        sub.userEmail?.toLowerCase().includes(q);
                                }
                                return true;
                            })
                            .map((sub) => (
                                <div key={sub.id} className={styles.projectCard}>
                                    <div className={styles.projectContent}>
                                        <div className={styles.projectHeader}>
                                            <h5 className={styles.projectTitle}>{sub.projectTitle}</h5>
                                            <span className={`badge ${
                                                sub.status === "approved" ? "bg-success" :
                                                sub.status === "rejected" ? "bg-danger" : "bg-warning text-dark"
                                            }`}>
                                                {sub.status === "approved" ? "Approved & Completed" :
                                                 sub.status === "rejected" ? "Rejected" : "Pending Approval"}
                                            </span>
                                        </div>

                                        <p className={styles.projectDescription}>
                                            <strong>Work Notes:</strong> {sub.submissionNotes || "No notes provided."}
                                        </p>

                                        <div className={styles.projectMeta} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                            <div className={styles.metaItem}>
                                                <User size={14} className="me-1" />
                                                <span><strong>Student:</strong> {sub.userName || "Student"} ({sub.userEmail})</span>
                                            </div>

                                            {/* 3 Clean Action Link Navigation Buttons */}
                                            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.25rem" }}>
                                                {sub.submissionUrl && (
                                                    <button
                                                        type="button"
                                                        onClick={() => openAdminViewer(`${sub.userName || 'Student'} — Demo Link`, sub.submissionUrl, "video")}
                                                        className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                                                        style={{ fontSize: "0.78rem", fontWeight: "600", padding: "0.25rem 0.6rem", borderRadius: "0.4rem" }}
                                                    >
                                                        <Globe size={13} /> Demo Link
                                                    </button>
                                                )}

                                                {sub.documentUrl && (
                                                    <button
                                                        type="button"
                                                        onClick={() => openAdminViewer(`${sub.userName || 'Student'} — Text Document`, sub.documentUrl, "pdf")}
                                                        className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1"
                                                        style={{ fontSize: "0.78rem", fontWeight: "600", padding: "0.25rem 0.6rem", borderRadius: "0.4rem" }}
                                                    >
                                                        <FileText size={13} /> Text Document
                                                    </button>
                                                )}

                                                {sub.githubUrl && (
                                                    <a
                                                        href={sub.githubUrl.startsWith("http") ? sub.githubUrl : `https://${sub.githubUrl}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm btn-outline-dark d-inline-flex align-items-center gap-1"
                                                        style={{ fontSize: "0.78rem", fontWeight: "600", padding: "0.25rem 0.6rem", borderRadius: "0.4rem" }}
                                                    >
                                                        <Zap size={13} /> GitHub Repo
                                                    </a>
                                                )}
                                            </div>
                                        </div>

                                        <div className={styles.projectFooter}>
                                            <span style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                                                Submitted: {sub.submittedAt?.toDate ? sub.submittedAt.toDate().toLocaleDateString() : "Recently"}
                                            </span>
                                            <div className={styles.projectActions} style={{ gap: "0.4rem" }}>
                                                {sub.status !== "approved" && (
                                                    <button
                                                        className="btn btn-sm btn-success d-flex align-items-center gap-1"
                                                        onClick={() => handleApproveSubmission(sub)}
                                                        title="Approve Quality & Mark Project Completed"
                                                        style={{ fontSize: "0.8rem", padding: "0.3rem 0.6rem" }}
                                                    >
                                                        <Check size={14} /> Approve Work
                                                    </button>
                                                )}
                                                {sub.status !== "rejected" && (
                                                    <button
                                                        className="btn btn-sm btn-warning text-dark d-flex align-items-center gap-1"
                                                        onClick={() => handleRejectSubmission(sub)}
                                                        title="Reject Submission"
                                                        style={{ fontSize: "0.8rem", padding: "0.3rem 0.6rem" }}
                                                    >
                                                        <X size={14} /> Reject
                                                    </button>
                                                )}
                                                <button
                                                    className={`${styles.actionBtn} ${styles.deleteActionBtn}`}
                                                    onClick={() => handleDeleteSubmission(sub.id)}
                                                    title="Delete Submission Record"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            ) : activeSubTab === "requests" ? (
                /* Self-Project Requests View */
                <div>
                    <div className={styles.filters}>
                        <div className={styles.searchBox}>
                            <Search size={18} className={styles.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search project requests..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={styles.searchInput}
                            />
                        </div>

                        <div className={styles.filterGroup}>
                            <select
                                value={requestFilterStatus}
                                onChange={(e) => setRequestFilterStatus(e.target.value)}
                                className={styles.filterSelect}
                            >
                                <option value="all">All Request Statuses</option>
                                <option value="pending">Pending Approval</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </select>
                        </div>
                    </div>

                    <div className={styles.stats}>
                        <div className={styles.statCard}>
                            <div className={styles.statNumber}>{projectRequests.length}</div>
                            <div className={styles.statLabel}>Total Requests</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statNumber} style={{ color: "#d97706" }}>
                                {projectRequests.filter(r => r.status === "pending").length}
                            </div>
                            <div className={styles.statLabel}>Pending Review</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statNumber} style={{ color: "#28a745" }}>
                                {projectRequests.filter(r => r.status === "approved").length}
                            </div>
                            <div className={styles.statLabel}>Approved & Mapped</div>
                        </div>
                    </div>

                    <div className={styles.projectGrid}>
                        {projectRequests
                            .filter(req => {
                                if (requestFilterStatus !== "all" && req.status !== requestFilterStatus) return false;
                                if (searchTerm) {
                                    const q = searchTerm.toLowerCase();
                                    return req.title?.toLowerCase().includes(q) ||
                                        req.userName?.toLowerCase().includes(q) ||
                                        req.userEmail?.toLowerCase().includes(q) ||
                                        req.category?.toLowerCase().includes(q);
                                }
                                return true;
                            })
                            .map((req) => (
                                <div key={req.id} className={styles.projectCard}>
                                    <div className={styles.projectContent}>
                                        <div className={styles.projectHeader}>
                                            <h5 className={styles.projectTitle}>{req.title}</h5>
                                            <span className={`badge ${
                                                req.status === "approved" ? "bg-success" :
                                                req.status === "rejected" ? "bg-danger" : "bg-warning text-dark"
                                            }`}>
                                                {req.status === "approved" ? "Approved" :
                                                 req.status === "rejected" ? "Rejected" : "Pending Review"}
                                            </span>
                                        </div>

                                        <p className={styles.projectDescription}>
                                            {req.description?.substring(0, 100)}...
                                        </p>

                                        <div className={styles.projectMeta}>
                                            <div className={styles.metaItem}>
                                                <User size={14} className="me-1" />
                                                <span><strong>Submitted By:</strong> {req.userName || "User"}</span>
                                            </div>
                                            <div className={styles.metaItem}>
                                                <Mail size={14} className="me-1" />
                                                <span>{req.userEmail}</span>
                                            </div>
                                            {req.category && (
                                                <div className={styles.metaItem}>
                                                    <Tag size={14} className="me-1" />
                                                    <span>{req.category}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className={styles.projectFooter}>
                                            <span style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                                                {req.createdAt?.toDate ? req.createdAt.toDate().toLocaleDateString() : "Recently"}
                                            </span>
                                            <div className={styles.projectActions} style={{ gap: "0.4rem" }}>
                                                {req.status !== "approved" && (
                                                    <button
                                                        className="btn btn-sm btn-success d-flex align-items-center gap-1"
                                                        onClick={() => handleApproveRequest(req)}
                                                        title="Approve & Map Project"
                                                        style={{ fontSize: "0.8rem", padding: "0.3rem 0.6rem" }}
                                                    >
                                                        <Check size={14} /> Approve
                                                    </button>
                                                )}
                                                {req.status !== "rejected" && (
                                                    <button
                                                        className="btn btn-sm btn-warning text-dark d-flex align-items-center gap-1"
                                                        onClick={() => handleRejectRequest(req)}
                                                        title="Reject Request"
                                                        style={{ fontSize: "0.8rem", padding: "0.3rem 0.6rem" }}
                                                    >
                                                        <X size={14} /> Reject
                                                    </button>
                                                )}
                                                <button
                                                    className={`${styles.actionBtn} ${styles.deleteActionBtn}`}
                                                    onClick={() => handleDeleteRequest(req.id)}
                                                    title="Delete Request"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            ) : (
                /* Lab Projects View */
                <div>
                    <div className={styles.filters}>
                        <div className={styles.searchBox}>
                            <Search size={18} className={styles.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search projects..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={styles.searchInput}
                            />
                        </div>

                        <div className={styles.filterGroup}>
                            <select
                                value={filterDifficulty}
                                onChange={(e) => setFilterDifficulty(e.target.value)}
                                className={styles.filterSelect}
                            >
                                <option value="all">All Difficulties</option>
                                {difficulties.map(difficulty => (
                                    <option key={difficulty} value={difficulty}>
                                        {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className={styles.stats}>
                        <div className={styles.statCard}>
                            <div className={styles.statNumber}>{projects.length}</div>
                            <div className={styles.statLabel}>Total Projects</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statNumber}>
                                {projects.filter(p => p.isPublished).length}
                            </div>
                            <div className={styles.statLabel}>Published</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statNumber}>
                                {projects.filter(p => p.pdfGuideUrl).length}
                            </div>
                            <div className={styles.statLabel}>With Guides</div>
                        </div>
                    </div>

                    <div className={styles.projectGrid}>
                        {filteredProjects.map((project) => (
                            <div key={project.id} className={styles.projectCard}>
                                <div className={styles.projectContent}>
                                    <div className={styles.projectHeader}>
                                        <h5 className={styles.projectTitle}>{project.title}</h5>
                                        <span className={`badge ${getDifficultyColor(project.difficulty)}`}>
                                            {project.difficulty}
                                        </span>
                                    </div>

                                    <p className={styles.projectDescription}>
                                        {project.description?.substring(0, 80)}...
                                    </p>

                                    <div className={styles.projectMeta}>
                                        {project.duration && (
                                            <div className={styles.metaItem}>
                                                <Clock size={14} className="me-1" />
                                                <span>{project.duration}</span>
                                            </div>
                                        )}
                                        {project.maxParticipants && (
                                            <div className={styles.metaItem}>
                                                <BookOpen size={14} className="me-1" />
                                                <span>Max {project.maxParticipants}</span>
                                            </div>
                                        )}
                                        {project.category && (
                                            <div className={styles.metaItem}>
                                                <Tag size={14} className="me-1" />
                                                <span>{project.category}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.projectFooter}>
                                        <div className={styles.badges}>
                                            {project.pdfGuideUrl && (
                                                <span className="badge bg-info">
                                                    <FileText size={12} className="me-1" />
                                                    Guide
                                                </span>
                                            )}
                                            <span className={`badge ${project.isPublished ? 'bg-success' : 'bg-secondary'}`}>
                                                {project.isPublished ? 'Published' : 'Draft'}
                                            </span>
                                        </div>
                                        <div className={styles.projectActions}>
                                            <button
                                                className={`${styles.actionBtn} ${styles.deleteActionBtn}`}
                                                onClick={() => {
                                                    setProjectToDelete(project);
                                                    setShowDeleteModal(true);
                                                }}
                                                title="Delete"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                            <button
                                                className={styles.actionBtn}
                                                onClick={() => handleEdit(project)}
                                                title="Edit"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                className={styles.viewActionBtn}
                                                onClick={() => handleView(project)}
                                                title="View Details"
                                            >
                                                <Eye size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredProjects.length === 0 && (
                        <div className={styles.emptyState}>
                            <Zap size={48} className="text-muted mb-3" />
                            <h5>No projects found</h5>
                            <p className="text-muted">
                                {searchTerm || filterDifficulty !== "all"
                                    ? "Try adjusting your search or filters"
                                    : "No projects have been created yet"
                                }
                            </p>
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    resetForm();
                                    setShowProjectModal(true);
                                }}
                            >
                                <Plus size={16} className="me-2" />
                                Create First Project
                            </button>
                        </div>
                    )}
                </div>
            )}

            {showBulkUploadModal && (
                <div className={styles.modalOverlay} onClick={() => setShowBulkUploadModal(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h5>Upload Projects (CSV)</h5>
                            <button
                                className={styles.closeBtn}
                                onClick={() => setShowBulkUploadModal(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <p>Upload a CSV containing multiple project rows. Use the template to ensure correct headers.</p>

                            <div className={styles.formGroup}>
                                <button
                                    type="button"
                                    className="btn btn-outline-primary me-2 mb-2"
                                    onClick={downloadCSVTemplate}
                                >
                                    <Download size={16} className="me-2" />
                                    Download Template
                                </button>
                                <div className="mb-3">
                                    <input
                                        type="file"
                                        accept=".csv"
                                        onChange={handleCSVUpload}
                                        className="form-control"
                                    />
                                </div>

                                {csvFile && (
                                    <div className="alert alert-info">
                                        <strong>Selected:</strong> {csvFile.name}
                                    </div>
                                )}

                                {csvUploadStatus.message && (
                                    <div className={`alert alert-${csvUploadStatus.type === 'error' ? 'danger' : csvUploadStatus.type === 'info' ? 'info' : 'success'}`}>
                                        {csvUploadStatus.message}
                                    </div>
                                )}

                                <div className={styles.modalActions}>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowBulkUploadModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-success"
                                        onClick={processBulkProjectCSV}
                                        disabled={!csvFile}
                                    >
                                        <Upload size={16} className="me-2" />
                                        Import Projects
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Project Modal */}
            {showProjectModal && (
                <div className={styles.modalOverlay} onClick={() => setShowProjectModal(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h5>{editingProject ? 'Edit Project' : 'Create New Project'}</h5>
                            <button
                                className={styles.closeBtn}
                                onClick={() => setShowProjectModal(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className={styles.modalBody}>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label>Project Title *</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={projectForm.title}
                                        onChange={handleInputChange}
                                        required
                                        className={styles.formInput}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Category *</label>
                                    <select
                                        name="category"
                                        value={projectForm.category}
                                        onChange={handleInputChange}
                                        required
                                        className={styles.formInput}
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map(category => (
                                            <option key={category} value={category}>{category}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Difficulty *</label>
                                    <select
                                        name="difficulty"
                                        value={projectForm.difficulty}
                                        onChange={handleInputChange}
                                        required
                                        className={styles.formInput}
                                    >
                                        {difficulties.map(difficulty => (
                                            <option key={difficulty} value={difficulty}>
                                                {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Duration</label>
                                    <input
                                        type="text"
                                        name="duration"
                                        value={projectForm.duration}
                                        onChange={handleInputChange}
                                        placeholder="e.g., 4 weeks, 40 hours"
                                        className={styles.formInput}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Max Participants</label>
                                    <input
                                        type="number"
                                        name="maxParticipants"
                                        value={projectForm.maxParticipants}
                                        onChange={handleInputChange}
                                        className={styles.formInput}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Silicon-Point Value</label>
                                    <input
                                        type="number"
                                        min="0"
                                        name="siliconPoints"
                                        value={projectForm.siliconPoints}
                                        onChange={handleInputChange}
                                        placeholder="50"
                                        className={styles.formInput}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Deadline</label>
                                    <input
                                        type="date"
                                        name="deadline"
                                        value={projectForm.deadline}
                                        onChange={handleInputChange}
                                        className={styles.formInput}
                                    />
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label>Description *</label>
                                <textarea
                                    name="description"
                                    value={projectForm.description}
                                    onChange={handleInputChange}
                                    required
                                    rows="4"
                                    className={styles.formInput}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Skills (comma-separated)</label>
                                <input
                                    type="text"
                                    value={projectForm.skills.join(', ')}
                                    onChange={handleSkillsChange}
                                    placeholder="e.g., Arduino, IoT, C++"
                                    className={styles.formInput}
                                />
                            </div>

                            {/* CSV upload removed from this modal — use Upload Projects button for bulk import */}

                            {/* Learning Outcomes */}
                            <div className={styles.formGroup}>
                                <label>What You'll Learn (4 Points)</label>
                                {[0, 1, 2, 3].map(idx => (
                                    <input
                                        key={idx}
                                        type="text"
                                        value={projectForm.learningOutcomes[idx]}
                                        onChange={(e) => {
                                            const newOutcomes = [...projectForm.learningOutcomes];
                                            newOutcomes[idx] = e.target.value;
                                            setProjectForm({ ...projectForm, learningOutcomes: newOutcomes });
                                        }}
                                        placeholder={`Learning outcome ${idx + 1}`}
                                        className={styles.formInput}
                                        style={{ marginBottom: '0.5rem' }}
                                    />
                                ))}
                            </div>

                            {/* Prerequisites */}
                            <div className={styles.formGroup}>
                                <label>Prerequisites (2 Points)</label>
                                {[0, 1].map(idx => (
                                    <input
                                        key={idx}
                                        type="text"
                                        value={projectForm.prerequisites[idx]}
                                        onChange={(e) => {
                                            const newPrereqs = [...projectForm.prerequisites];
                                            newPrereqs[idx] = e.target.value;
                                            setProjectForm({ ...projectForm, prerequisites: newPrereqs });
                                        }}
                                        placeholder={`Prerequisite ${idx + 1}`}
                                        className={styles.formInput}
                                        style={{ marginBottom: '0.5rem' }}
                                    />
                                ))}
                            </div>

                            {/* PDF Upload / Direct PDF URL */}
                            <div className={styles.formGroup}>
                                <label>Project Guide or Lab Module (PDF / File Link)</label>
                                <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", marginBottom: "0.5rem", flexWrap: "wrap" }}>
                                    <div>
                                        <input
                                            type="file"
                                            accept="application/pdf"
                                            onChange={handlePDFUpload}
                                            className={styles.fileInput}
                                            id="project-pdf"
                                        />
                                        <label htmlFor="project-pdf" className={styles.fileLabel} style={{ margin: 0, whiteSpace: "nowrap" }}>
                                            <Upload size={16} className="me-2" />
                                            {projectForm.pdfGuideUrl ? 'Change PDF File' : 'Upload PDF File'}
                                        </label>
                                    </div>
                                    <span style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: "bold" }}>OR</span>
                                    <input
                                        type="url"
                                        name="pdfGuideUrl"
                                        value={projectForm.pdfGuideUrl}
                                        onChange={handleInputChange}
                                        placeholder="Paste direct PDF URL or Google Drive link..."
                                        className={styles.formInput}
                                        style={{ flex: 1, minWidth: "220px" }}
                                    />
                                </div>

                                {uploadingPDF && (
                                    <div className={styles.uploadProgress}>
                                        <div className="progress" style={{ height: "8px" }}>
                                            <div
                                                className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                                style={{ width: `${pdfProgress}%` }}
                                            />
                                        </div>
                                        <small>{pdfProgress.toFixed(0)}% uploaded</small>
                                    </div>
                                )}

                                {projectForm.pdfGuideUrl && (
                                    <div className={styles.pdfPreview} style={{ marginTop: "0.5rem" }}>
                                        <FileText size={18} className="me-2" color="#059669" />
                                        <div>
                                            <strong style={{ fontSize: "0.85rem" }}>PDF Guide Attached</strong>
                                            <a href={projectForm.pdfGuideUrl} target="_blank" rel="noopener noreferrer" className="ms-2 badge bg-success" style={{ textDecoration: "none" }}>
                                                View PDF Link
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Video Guide (Upload Video File OR Paste Video URL) */}
                            <div className={styles.formGroup}>
                                <label>Video Guide / Tutorial (Video File / URL Link)</label>
                                <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", marginBottom: "0.5rem", flexWrap: "wrap" }}>
                                    <div>
                                        <input
                                            type="file"
                                            accept="video/*"
                                            onChange={handleVideoUpload}
                                            className={styles.fileInput}
                                            id="project-video"
                                        />
                                        <label htmlFor="project-video" className={styles.fileLabel} style={{ margin: 0, whiteSpace: "nowrap" }}>
                                            <Upload size={16} className="me-2" />
                                            {projectForm.videoUrl ? 'Change Video File' : 'Upload Video File'}
                                        </label>
                                    </div>
                                    <span style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: "bold" }}>OR</span>
                                    <input
                                        type="url"
                                        name="videoUrl"
                                        value={projectForm.videoUrl}
                                        onChange={handleInputChange}
                                        placeholder="Paste video URL, YouTube, or Drive link..."
                                        className={styles.formInput}
                                        style={{ flex: 1, minWidth: "220px" }}
                                    />
                                </div>

                                {uploadingVideo && (
                                    <div className={styles.uploadProgress}>
                                        <div className="progress" style={{ height: "8px" }}>
                                            <div
                                                className="progress-bar progress-bar-striped progress-bar-animated bg-danger"
                                                style={{ width: `${videoProgress}%` }}
                                            />
                                        </div>
                                        <small>{videoProgress.toFixed(0)}% uploaded</small>
                                    </div>
                                )}

                                {projectForm.videoUrl && (
                                    <div className={styles.pdfPreview} style={{ marginTop: "0.5rem" }}>
                                        <Play size={18} className="me-2" color="#e11d48" />
                                        <div>
                                            <strong style={{ fontSize: "0.85rem" }}>Video Guide Attached</strong>
                                            <a href={projectForm.videoUrl} target="_blank" rel="noopener noreferrer" className="ms-2 badge bg-danger" style={{ textDecoration: "none" }}>
                                                Watch Video Link
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="isPublished"
                                        checked={projectForm.isPublished}
                                        onChange={handleInputChange}
                                        className={styles.checkbox}
                                    />
                                    <span>Publish Project</span>
                                </label>
                            </div>

                            <div className={styles.modalActions}>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowProjectModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                >
                                    <Save size={16} className="me-2" />
                                    {editingProject ? 'Update Project' : 'Create Project'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {showDeleteModal && projectToDelete && (
                <div className={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h5>Confirm Deletion</h5>
                            <button
                                className={styles.closeBtn}
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <p>
                                Are you sure you want to delete the project <strong>"{projectToDelete.title}"</strong>?
                            </p>
                            <p className="text-danger">
                                This action cannot be undone and will permanently remove all project data and guides.
                            </p>
                            <div className={styles.modalActions}>
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-danger"
                                    onClick={handleDelete}
                                >
                                    Delete Project
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Project Modal */}
            {showViewModal && viewingProject && (
                <div className={styles.modalOverlay} onClick={() => { setShowViewModal(false); setViewingProject(null); }}>
                    <div className={styles.viewModalContent} onClick={(e) => e.stopPropagation()}>
                        <button
                            className={styles.closeBtn}
                            onClick={() => { setShowViewModal(false); setViewingProject(null); }}
                        >
                            <X size={24} />
                        </button>

                        {/* Modal Header */}
                        <div className={styles.viewModalHeader}>
                            <div>
                                <h2 className={styles.viewModalTitle}>{viewingProject.title}</h2>
                                <p className={styles.viewModalCategory}>{viewingProject.category}</p>
                            </div>
                            <span
                                className={styles.viewModalDifficultyBadge}
                                style={{ backgroundColor: getDifficultyColorHex(viewingProject.difficulty) }}
                            >
                                {viewingProject.difficulty}
                            </span>
                        </div>

                        {/* Modal Body */}
                        <div className={styles.viewModalBody}>
                            {/* Description */}
                            <div className={styles.viewSection}>
                                <h4 className={styles.viewSectionTitle}>📝 About Project</h4>
                                <p className={styles.viewDescriptionText}>{viewingProject.description}</p>
                            </div>

                            {/* Project Meta */}
                            <div className={styles.viewMetaSection}>
                                {viewingProject.duration && (
                                    <div className={styles.viewMetaBox}>
                                        <Clock size={18} className={styles.viewMetaIcon} />
                                        <div>
                                            <p className={styles.viewMetaLabel}>Duration</p>
                                            <p className={styles.viewMetaValue}>{viewingProject.duration}</p>
                                        </div>
                                    </div>
                                )}
                                {viewingProject.maxParticipants && (
                                    <div className={styles.viewMetaBox}>
                                        <Zap size={18} className={styles.viewMetaIcon} />
                                        <div>
                                            <p className={styles.viewMetaLabel}>Max Participants</p>
                                            <p className={styles.viewMetaValue}>{viewingProject.maxParticipants}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Skills Required */}
                            {viewingProject.skills && viewingProject.skills.length > 0 && (
                                <div className={styles.viewSection}>
                                    <h4 className={styles.viewSectionTitle}>🛠️ Skills Required</h4>
                                    <div className={styles.viewSkillsList}>
                                        {viewingProject.skills.map((skill, idx) => (
                                            <span key={idx} className={styles.viewSkillTag}>{skill}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Learning Outcomes */}
                            {viewingProject.learningOutcomes && viewingProject.learningOutcomes.some(o => o && o.trim()) && (
                                <div className={styles.viewSection}>
                                    <h4 className={styles.viewSectionTitle}>📚 What You'll Learn</h4>
                                    <ul className={styles.viewOutcomesList}>
                                        {viewingProject.learningOutcomes.filter(o => o && o.trim()).map((outcome, idx) => (
                                            <li key={idx}><span className={styles.viewCheckmark}>✓</span> {outcome}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Prerequisites */}
                            {viewingProject.prerequisites && viewingProject.prerequisites.some(p => p && p.trim()) && (
                                <div className={styles.viewSection}>
                                    <h4 className={styles.viewSectionTitle}>⚡ Prerequisites</h4>
                                    <ul className={styles.viewPrerequisitesList}>
                                        {viewingProject.prerequisites.filter(p => p && p.trim()).map((prereq, idx) => (
                                            <li key={idx}><span className={styles.viewDot}>•</span> {prereq}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Resources Section */}
                            <div className={styles.viewResourcesSection}>
                                <h4 className={styles.viewSectionTitle}>📦 Resources</h4>
                                <div className={styles.viewResourcesGrid}>
                                    {viewingProject.pdfGuideUrl && (
                                        <a
                                            href={viewingProject.pdfGuideUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.viewResourceCard}
                                        >
                                            <FileText size={28} className={styles.viewResourceIcon} />
                                            <span>PDF Guide</span>
                                        </a>
                                    )}
                                    {viewingProject.videoUrl && (
                                        <a
                                            href={viewingProject.videoUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.viewResourceCard}
                                        >
                                            <Play size={28} className={styles.viewResourceIcon} />
                                            <span>Video Tutorial</span>
                                        </a>
                                    )}
                                    {viewingProject.labModuleUrl && (
                                        <a
                                            href={viewingProject.labModuleUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.viewResourceCard}
                                        >
                                            <BookOpen size={28} className={styles.viewResourceIcon} />
                                            <span>Lab Module</span>
                                        </a>
                                    )}
                                </div>
                                {!viewingProject.pdfGuideUrl && !viewingProject.videoUrl && !viewingProject.labModuleUrl && (
                                    <p className={styles.viewNoResources}>No resources available yet</p>
                                )}
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className={styles.viewModalFooter}>
                            <button className={styles.viewStartProjectBtn}>
                                <Play size={18} />
                                View in User Portal
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* In-Page Resource Viewer Modal for Admin */}
            <ResourceViewerModal
                show={adminViewerData.show}
                onClose={() => setAdminViewerData({ ...adminViewerData, show: false })}
                title={adminViewerData.title}
                url={adminViewerData.url}
                type={adminViewerData.type}
            />
        </div>
    );
}

export default ProjectManagement;
